/**
 * Uploading files to a capture session.
 *
 * Uploads run one at a time and in order. Parallel uploads would finish sooner
 * on a fast link and would also make the progress display meaningless on the
 * slow ones this is actually used over - and a session's files are usually
 * dozens of frames from one turn of the head, where order is easier to reason
 * about than speed.
 *
 * Each file's progress is the real number reported by the browser. Nothing
 * here animates on a timer.
 *
 * Three things this screen learned from watching someone use it:
 *
 * **A failure has to say what actually failed.** Nine of ten photographs were
 * stored and the tenth - a PNG renamed to `.jpg` - was refused with "check the
 * fields" on a screen that has no fields. The sentence now comes from the
 * server's refusal code via `uploadFailure`, and names the remedy.
 *
 * **The batch has a state, not just each file.** Twenty rows each with their
 * own bar answered "how is this file doing?" twenty times and never answered
 * "how is this going?". The summary above the list is counts, because counts
 * are what is actually known - no byte-level total is fabricated.
 *
 * **Stopping is real or it is not offered.** `uploadMedia` takes an
 * `AbortSignal` and aborts the underlying request, so the button aborts the
 * transfer in flight and stops the queue behind it. It does not hide a request
 * that keeps running.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { uploadMedia } from "@/api/client";
import { ApiError } from "@/api/errors";
import { Button, Callout } from "@/components/ui";
import { rejectionReason, useMediaCapabilities } from "@/lib/useMediaCapabilities";
import { formatBytes } from "@/lib/format";
import { SupportedFormats } from "./SupportedFormats";
import { uploadFailure, type UploadFailure } from "./uploadFailure";
import type { SubjectType } from "@/api/types";
import { GUIDED_REGIONS } from "@/features/coverage/guidedCoverage";
import "./sessions.css";

type ItemState =
  | { phase: "waiting" }
  | { phase: "uploading"; fraction: number }
  | { phase: "done" }
  | { phase: "canceled" }
  | { phase: "failed"; failure: UploadFailure };

interface Item {
  key: string;
  file: File;
  state: ItemState;
}

/** What the project has, and what a reconstruction will do with it. */
export interface CaptureExpectation {
  /** Photographs already in this project, from the readiness endpoint. */
  photos: number;
  /** The floor below which no reconstruction can run. */
  minimum: number;
  /** The most views a surface model option will take. */
  maximum: number;
}

export function MediaUpload({
  sessionId,
  subjectType,
  onUploaded,
  expectation = null,
}: {
  sessionId: string;
  subjectType: SubjectType;
  onUploaded: () => void;
  /** Null while readiness has not been read. No numbers are invented. */
  expectation?: CaptureExpectation | null;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const counter = useRef(0);
  const capabilities = useMediaCapabilities();
  const [formatsOpen, setFormatsOpen] = useState(false);
  // Which categories the picker should offer. Set immediately before the
  // dialog opens, so the button the user pressed decides what they see.
  const [accept, setAccept] = useState<string | null>(null);
  const [guidedRegion, setGuidedRegion] = useState("");
  /** The transfer in flight, so stopping actually aborts it. */
  const active = useRef<AbortController | null>(null);
  /** Set by the stop button; read by the queue between files. */
  const stopped = useRef(false);

  useEffect(
    () => () => {
      // Leaving the screen aborts the transfer rather than leaving an
      // XMLHttpRequest running against a component that is gone.
      active.current?.abort();
    },
    [],
  );

  const open = useCallback((filter: string) => {
    setAccept(filter);
    // After the state has been applied to the input, or the dialog opens with
    // the previous filter still on it.
    window.setTimeout(() => input.current?.click(), 0);
  }, []);

  const update = useCallback((key: string, state: ItemState) => {
    setItems((current) => current.map((item) => (item.key === key ? { ...item, state } : item)));
  }, []);

  /**
   * Upload a set of items, one at a time.
   *
   * Extracted so a retry runs *this* code rather than a second copy of it. A
   * retried file goes through the same request, the same progress reporting
   * and the same failure classification as its first attempt, which is what
   * makes "tekrar dene" mean the same thing as "yükle".
   */
  const run = useCallback(
    async (queued: Item[]) => {
      setBusy(true);
      let anySucceeded = false;
      for (const item of queued) {
        if (item.state.phase !== "waiting") continue;
        if (stopped.current) {
          update(item.key, { phase: "canceled" });
          continue;
        }
        const controller = new AbortController();
        active.current = controller;
        update(item.key, { phase: "uploading", fraction: 0 });
        try {
          await uploadMedia(
            sessionId,
            item.file,
            {
              onProgress: (fraction) => update(item.key, { phase: "uploading", fraction }),
              signal: controller.signal,
            },
            guidedRegion || null,
          );
          update(item.key, { phase: "done" });
          anySucceeded = true;
        } catch (caught) {
          const error =
            caught instanceof ApiError ? caught : new ApiError({ kind: "unknown", url: "" });
          // Diagnostics to the console, product language to the screen.
          console.error("[sentyform] yükleme hatası", error.kind, error.status, error.code, error.detail);
          update(item.key, error.kind === "canceled"
            ? { phase: "canceled" }
            : { phase: "failed", failure: uploadFailure(error) });
        } finally {
          active.current = null;
        }
      }
      setBusy(false);
      // Refresh once at the end rather than per file: the list below is a
      // server read, and a request per frame would be unkind to it. Also runs
      // after a stop - a file that reached the server before the abort has to
      // appear rather than be quietly missing.
      if (anySucceeded || stopped.current) onUploaded();
    },
    [sessionId, onUploaded, update, guidedRegion],
  );

  const add = useCallback(
    async (files: FileList | File[]) => {
      stopped.current = false;
      const queued: Item[] = Array.from(files).map((file) => {
        const reason = rejectionReason(file, capabilities);
        return {
          key: `${++counter.current}`,
          file,
          state: reason
            ? ({
                phase: "failed",
                failure: {
                  what: reason,
                  next: "Dosyayı desteklenen bir biçimde kaydedip tekrar seçin.",
                },
              } as const)
            : ({ phase: "waiting" } as const),
        };
      });
      setItems((current) => [...current, ...queued]);
      await run(queued);
    },
    [capabilities, run],
  );

  /**
   * Try the failed files again, and only those.
   *
   * Only those, because the ones that succeeded are on the server: re-sending
   * them would create duplicates of a customer's photographs, which is a worse
   * outcome than the failure being retried.
   *
   * A file the *browser* refused - wrong format, too large - is not retried
   * either. Nothing about it has changed and a second attempt would fail the
   * same way, which teaches people the button does not work.
   */
  const retryFailed = useCallback(async () => {
    const retryable = items.filter(
      (item) =>
        item.state.phase === "failed" && !rejectionReason(item.file, capabilities),
    );
    if (retryable.length === 0) return;
    stopped.current = false;
    const reset = retryable.map((item) => ({ ...item, state: { phase: "waiting" } as const }));
    setItems((current) =>
      current.map((item) => {
        const found = reset.find((entry) => entry.key === item.key);
        return found ?? item;
      }),
    );
    await run(reset);
  }, [items, capabilities, run]);

  /** Failures a second attempt could plausibly change. See `retryFailed`. */
  const retryableCount = items.filter(
    (item) => item.state.phase === "failed" && !rejectionReason(item.file, capabilities),
  ).length;

  const stop = useCallback(() => {
    stopped.current = true;
    active.current?.abort();
  }, []);

  const tally = summarise(items);
  const regions = subjectType === "human_head" ? [] : GUIDED_REGIONS[subjectType];

  return (
    <section className="stack">
      {expectation && <CaptureExpectationNote expectation={expectation} />}

      {regions.length > 0 && (
        <label className="field">
          <span>Bu yükleme hangi çekim açısı için?</span>
          <select value={guidedRegion} onChange={(event) => setGuidedRegion(event.target.value)}>
            <option value="">Şimdi seçmeyeceğim</option>
            {regions.map((region) => (
              <option key={region.key} value={region.key}>
                {region.label}
              </option>
            ))}
          </select>
          <small className="muted">
            {guidedRegion
              ? "Bu yüklemedeki tüm fotoğraflar bu açıya işaretlenir. Sonradan Kapsama ekranından değiştirebilirsiniz."
              : "Seçmezseniz fotoğraflar yüklenir ve hiçbir açıya işaretlenmez; Kapsama ekranından toplu olarak işaretleyebilirsiniz."}
          </small>
        </label>
      )}
      <div
        className={dragging ? "dropzone dropzone--active" : "dropzone"}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (event.dataTransfer.files.length > 0) void add(event.dataTransfer.files);
        }}
      >
        <p>Dosyaları buraya sürükleyin</p>
        {/*
          Three buttons rather than one, because "Dosya seç" made the user
          guess what this screen wants. Each opens the same picker with a
          narrower `accept`, which is a hint to the OS dialog and nothing more -
          the backend re-derives the extension, checks the MIME type and probes
          the bytes regardless. No wildcard accept is used anywhere, and no
          option here names something the server would refuse.
        */}
        <div className="row">
          <Button onClick={() => open(capabilities.photoAccept)} busy={busy}>
            Fotoğraf Yükle
          </Button>
          <Button onClick={() => open(capabilities.videoAccept)} busy={busy}>
            Video Yükle
          </Button>
          <Button onClick={() => open(capabilities.accept)} busy={busy}>
            Tüm Desteklenen Medya
          </Button>
        </div>
        {/*
          One affordance rather than twenty menu entries. The dialog lists what
          the server actually accepts, including which RAW families and what
          AVCHD means here - detail nobody needs until they need it exactly.
        */}
        <button
          type="button"
          className="dropzone__formats"
          onClick={() => setFormatsOpen(true)}
        >
          Desteklenen biçimler
        </button>
        <input
          ref={input}
          type="file"
          multiple
          accept={accept ?? capabilities.accept}
          className="visually-hidden"
          onChange={(event) => {
            if (event.target.files) void add(event.target.files);
            // Allow re-selecting the same file after a failure.
            event.target.value = "";
          }}
        />
      </div>

      {formatsOpen && <SupportedFormats onClose={() => setFormatsOpen(false)} />}

      {items.length > 0 && (
        <div className="upload-batch">
          {/*
            Counts, not a fabricated percentage. A byte-level total across a
            queue whose files have not been read yet would be a number this
            screen cannot stand behind; "12 / 20 tamamlandı" is one it can.
          */}
          <p className="upload-batch__summary" role="status" aria-live="polite">
            {summaryText(tally)}
          </p>
          {busy && (
            <Button onClick={stop}>Yüklemeyi durdur</Button>
          )}
        </div>
      )}

      {tally.failed > 0 && !busy && (
        <Callout tone="caution" title={`${tally.failed} dosya yüklenemedi`}>
          <div className="stack-tight">
            <span>
              Yüklenen {tally.done} dosya kaydedildi ve listede duruyor. Aşağıda
              her başarısız dosyanın nedeni ve ne yapmanız gerektiği yazıyor.
            </span>
            {/*
              Only the failures, and only the ones a second attempt could
              change: re-sending the files that arrived would duplicate a
              customer's photographs, and retrying one the browser refused for
              its format would fail identically.
            */}
            {retryableCount > 0 && (
              <div>
                <Button variant="secondary" onClick={() => void retryFailed()}>
                  {retryableCount === tally.failed
                    ? `${retryableCount} dosyayı tekrar dene`
                    : `Tekrar denenebilir ${retryableCount} dosyayı dene`}
                </Button>
              </div>
            )}
          </div>
        </Callout>
      )}

      {items.length > 0 && (
        <ul className="uploadlist">
          {items.map((item) => (
            <UploadRow key={item.key} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * How many photographs to aim for, using the server's own numbers.
 *
 * Every figure here comes from the readiness endpoint - the floor a
 * reconstruction refuses below, and the ceiling the surface model options
 * read. Nothing claims that more photographs produce a better model: that is
 * not measured, and the profiles differ in *time budget* rather than in
 * promised quality.
 */
function CaptureExpectationNote({ expectation }: { expectation: CaptureExpectation }) {
  const { photos, minimum, maximum } = expectation;
  return (
    <Callout tone={photos < minimum ? "caution" : "neutral"} title="Kaç fotoğraf gerekiyor?">
      <ul className="upload-guidance">
        <li>
          Bu projede şu anda <strong>{photos} fotoğraf</strong> var.
        </li>
        <li>
          Çok az fotoğraf, konunun çevresinde boşluk bırakır ve model
          oluşturulamaz: en az <strong>{minimum} görüntü</strong> gerekir.
        </li>
        <li>
          Yüzey modeli seçenekleri en çok <strong>{maximum} görüntü</strong>{" "}
          kullanır. Daha fazlasını yükleyebilirsiniz; sistem yüklenen her
          fotoğrafı kullanmak zorunda değildir.
        </li>
        <li>
          Konunun çevresini dönerek, komşu kareler birbiriyle örtüşecek şekilde
          çekin.
        </li>
      </ul>
    </Callout>
  );
}

interface Tally {
  total: number;
  done: number;
  failed: number;
  uploading: number;
  waiting: number;
  canceled: number;
}

export function summarise(items: readonly { state: ItemState }[]): Tally {
  const tally: Tally = { total: items.length, done: 0, failed: 0, uploading: 0, waiting: 0, canceled: 0 };
  for (const { state } of items) {
    if (state.phase === "done") tally.done += 1;
    else if (state.phase === "failed") tally.failed += 1;
    else if (state.phase === "uploading") tally.uploading += 1;
    else if (state.phase === "waiting") tally.waiting += 1;
    else tally.canceled += 1;
  }
  return tally;
}

/** Only the parts that are actually happening. A zero is not worth a clause. */
export function summaryText(tally: Tally): string {
  const parts = [`${tally.done} / ${tally.total} tamamlandı`];
  if (tally.failed > 0) parts.push(`${tally.failed} başarısız`);
  if (tally.uploading > 0) parts.push(`${tally.uploading} yükleniyor`);
  if (tally.waiting > 0) parts.push(`${tally.waiting} sırada`);
  if (tally.canceled > 0) parts.push(`${tally.canceled} durduruldu`);
  return parts.join(" · ");
}

function UploadRow({ item }: { item: Item }) {
  const { file, state } = item;
  return (
    <li className="uploadrow">
      <span className="uploadrow__name">{file.name}</span>
      <span className="faint">{formatBytes(file.size)}</span>
      {state.phase === "uploading" && (
        <>
          <span className="uploadrow__bar">
            <span
              className="meter"
              role="progressbar"
              aria-valuenow={Math.round(state.fraction * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${file.name} yükleniyor`}
              style={{ display: "block" }}
            >
              <span
                className="meter__fill meter__fill--success"
                style={{ width: `${state.fraction * 100}%`, display: "block" }}
              />
            </span>
          </span>
          <span className="faint" style={{ gridColumn: "1 / -1" }}>
            %{Math.round(state.fraction * 100)} yüklendi
          </span>
        </>
      )}
      {state.phase === "waiting" && <span className="faint" style={{ gridColumn: "1 / -1" }}>Sırada</span>}
      {state.phase === "done" && (
        <span className="faint" style={{ gridColumn: "1 / -1", color: "var(--tone-success)" }}>
          Yüklendi
        </span>
      )}
      {state.phase === "canceled" && (
        <span className="faint" style={{ gridColumn: "1 / -1" }}>
          Durduruldu — bu dosya yüklenmedi.
        </span>
      )}
      {state.phase === "failed" && (
        <span className="uploadrow__bar">
          {/*
            Two sentences, deliberately: what happened, and what to do about
            it. A cause with no remedy leaves the reader exactly where the
            generic message did.
          */}
          <Callout tone="danger">
            <span className="stack-tight">
              <span>{state.failure.what}</span>
              <span className="faint">{state.failure.next}</span>
            </span>
          </Callout>
        </span>
      )}
    </li>
  );
}
